<template>
    <NuxtLink
        :to="`/blog/${post.slug}`"
        class="group flex-shrink-0 w-[320px] bg-white dark:bg-surface-card rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
    >
        <!-- Featured Image -->
        <div class="relative h-[160px] overflow-hidden bg-gray-100 dark:bg-surface-card">
            <img
                v-if="post.featuredImage"
                :src="blogImageSrc(post.featuredImage, 320, 160)"
                :srcset="blogImageSrcset(post.featuredImage, 320, 160)"
                :alt="post.featuredImageAlt || post.title"
                width="320"
                height="160"
                loading="lazy"
                decoding="async"
                class="size-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div v-else class="size-full flex items-center justify-center text-gray-400 dark:text-txt-caption">
                <svg class="size-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
            </div>
            <!-- Reading time badge -->
            <div v-if="post.readingTime" class="absolute bottom-3 right-3 px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full">
                {{ post.readingTime }} min
            </div>
        </div>

        <!-- Content -->
        <div class="p-5">
            <!-- Tags -->
            <div v-if="post.tags && post.tags.length > 0" class="flex flex-wrap gap-1.5 mb-3">
                <span
                    v-for="tag in post.tags.slice(0, 2)"
                    :key="tag"
                    class="px-2 py-0.5 text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-full"
                >
                    {{ tag }}
                </span>
            </div>

            <!-- Title -->
            <h3 class="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {{ post.title }}
            </h3>

            <!-- Excerpt -->
            <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4">
                {{ post.excerpt }}
            </p>

            <!-- Author & Date (reading time shown on image badge) -->
            <BlogPostMeta
                :author="post.author"
                :published-at="post.publishedAt"
            />
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import type { BlogPost } from '#shared/types/Blog'
import { blogImageSrc, blogImageSrcset } from '@/utils/blogImage'

defineProps<{
    post: BlogPost
}>()
</script>
